import type { SignupGoalId } from '../constants/signupGoals';
import { getStoredSignupGoal, getSignupGoalOption } from '../constants/signupGoals';

export const GOAL_POST_REGISTRATION_PATHS: Record<SignupGoalId, string> = {
  buyer_audit: '/buyer-audit',
  brsr_compliance: '/compliance',
  baseline_footprint: '/carbon-footprint?section=start-assessment',
  green_finance: '/carbon-savings?section=schemes',
  pat_icm: '/carbon-marketplace',
  cost_reduction: '/carbon-footprint?section=recommendations',
};

export const GOAL_POST_PROFILE_PATHS: Record<SignupGoalId, string> = {
  buyer_audit: '/document-upload',
  brsr_compliance: '/document-upload',
  baseline_footprint: '/document-upload',
  green_finance: '/document-upload',
  pat_icm: '/document-upload',
  cost_reduction: '/document-upload',
};

/** First screen after MSME company registration — always confirm profile before bills. */
export const resolveMsmeRegistrationSuccessPath = (_goal: SignupGoalId | null): string =>
  '/msme-profile';

/**
 * Next path after company profile is complete — respects signup goal while keeping
 * document upload as the default evidence step when needed.
 */
export const resolveMsmeGoalAwareNextPath = (
  goal: SignupGoalId | null,
  context: { profileComplete: boolean; hasDocuments?: boolean }
): string => {
  if (!context.profileComplete) {
    return '/msme-profile';
  }

  const stored = goal || getStoredSignupGoal();
  if (!stored) {
    return '/document-upload';
  }

  const hasDocuments = Boolean(context.hasDocuments);

  switch (stored) {
    case 'green_finance':
      return hasDocuments ? '/carbon-savings?section=schemes' : '/document-upload';
    case 'buyer_audit':
      return hasDocuments ? '/buyer-audit' : '/document-upload';
    case 'brsr_compliance':
      return hasDocuments ? '/compliance' : '/document-upload';
    case 'pat_icm':
      return hasDocuments ? '/operations' : '/document-upload';
    case 'cost_reduction':
      return hasDocuments
        ? '/carbon-footprint?section=recommendations'
        : '/document-upload';
    case 'baseline_footprint':
    default:
      return '/document-upload';
  }
};

export const resolveGoalDestination = (
  goal: SignupGoalId | null,
  fallback = '/dashboard'
): string => {
  if (!goal) return fallback;
  return GOAL_POST_REGISTRATION_PATHS[goal] || fallback;
};

/** @deprecated Prefer resolveMsmeOnboardingDestination with full onboarding state. */
export const resolvePostProfileDestination = (
  goal: SignupGoalId | null,
  workflowCompleted: boolean
): string => {
  const stored = goal || getStoredSignupGoal();
  if (workflowCompleted) {
    return resolveGoalDestination(stored, '/dashboard');
  }
  if (!stored) {
    return '/document-upload';
  }
  return GOAL_POST_PROFILE_PATHS[stored] || '/document-upload';
};

export const getGoalHint = (goal: SignupGoalId | null): string | undefined =>
  getSignupGoalOption(goal)?.nextStepHint;
